import React, { useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FileUploadZone = ({ onFileSelect, acceptedFormats = '.jpg,.jpeg,.png,.pdf', maxSize = 10 }) => {
  const fileInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => prev + 1);
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => prev - 1);
    if (dragCounter === 1) {
      setIsDragOver(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    setDragCounter(0);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelection(files[0]);
    }
  };

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleFileSelection(files[0]);
    }
  };

  const handleFileSelection = (file) => {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      onFileSelect(null, {
        type: 'error',
        message: 'Format de fichier non supporté. Veuillez utiliser JPG, PNG ou PDF.'
      });
      return;
    }

    // Validate file size (convert MB to bytes)
    const maxSizeBytes = maxSize * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      onFileSelect(null, {
        type: 'error',
        message: `La taille du fichier dépasse ${maxSize}MB. Veuillez choisir un fichier plus petit.`
      });
      return;
    }

    onFileSelect(file, { type: 'success', message: 'Fichier sélectionné avec succès' });
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-4">
      <div
        className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-smooth cursor-pointer ${
          isDragOver
            ? 'border-primary bg-primary-50 scale-105' :'border-border hover:border-primary-200 hover:bg-secondary-50'
        }`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={openFileDialog}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openFileDialog();
          }
        }}
        aria-label="Zone de téléchargement de document - Cliquez ou glissez un fichier"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedFormats}
          onChange={handleFileInputChange}
          className="hidden"
          aria-hidden="true"
        />

        <div className="space-y-4">
          {/* Upload Icon */}
          <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center ${
            isDragOver ? 'bg-primary text-primary-foreground' : 'bg-secondary-100 text-text-secondary'
          }`}>
            <Icon name={isDragOver ? 'Download' : 'Upload'} size={32} />
          </div>

          {/* Main Text */}
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-text-primary font-inter">
              {isDragOver ? 'Déposez votre document ici' : 'Glissez votre document ici'}
            </h3>
            <p className="text-text-secondary font-inter">
              ou cliquez pour sélectionner un fichier
            </p>
          </div>

          {/* File Format Info */}
          <div className="space-y-2">
            <div className="flex items-center justify-center space-x-4 text-sm text-text-muted font-inter">
              <span className="flex items-center space-x-1">
                <Icon name="FileImage" size={16} />
                <span>JPG, PNG</span>
              </span>
              <span className="w-1 h-1 bg-border rounded-full" />
              <span className="flex items-center space-x-1">
                <Icon name="FileText" size={16} />
                <span>PDF</span>
              </span>
            </div>
            <p className="text-xs text-text-muted font-inter">
              Taille maximale: {maxSize}MB
            </p>
          </div>

          {/* Upload Button for Mobile */}
          <div className="sm:hidden pt-2">
            <Button
              variant="outline"
              iconName="Upload"
              iconPosition="left"
              onClick={(e) => {
                e.stopPropagation();
                openFileDialog();
              }}
            >
              Choisir un fichier
            </Button>
          </div>
        </div>

        {/* Drag Overlay */}
        {isDragOver && (
          <div className="absolute inset-0 bg-primary/10 rounded-xl flex items-center justify-center">
            <div className="bg-primary text-primary-foreground px-4 py-2 rounded-lg font-medium font-inter">
              Déposez le fichier maintenant
            </div>
          </div>
        )}
      </div>

      {/* Additional Instructions */}
      <div className="bg-secondary-50 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={20} color="#64748B" className="flex-shrink-0 mt-0.5" />
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-text-primary font-inter">
              Instructions importantes
            </h4>
            <ul className="text-sm text-text-secondary font-inter space-y-1">
              <li>• Assurez-vous que le document est lisible et bien éclairé</li>
              <li>• Évitez les reflets et les ombres sur le document</li>
              <li>• Le document doit être entièrement visible dans l'image</li>
              <li>• Formats acceptés: JPG, PNG, PDF (max {maxSize}MB)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileUploadZone;